import MarkdownIt from 'markdown-it';
import type {Slide} from '../core/model';
import type {LayoutStructure} from '../core/presentation-registry';
import {createVideo, videoLink} from './video';
export {youtubeEmbedUrl} from './video';

const markdown = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true,
  breaks: false,
});

type RenderOptions = {
  resolveAsset(source: string): string;
  structure?: LayoutStructure;
};

type CustomMedia = {
  kind: 'animation' | 'video';
  source: string;
  alt: string;
  title: string;
  attrs: string;
};

const CUSTOM_MEDIA =
  /!\[([^\]]*)\]\((animation|video):([^)\s"]+)(?:\s+"([^"]*)")?\)(?:\{([^}]*)\})?/g;

function preprocess(source: string) {
  const media: CustomMedia[] = [];
  const lines: Array<{text: string; start: number; end: number}> = [];
  let offset = 0;
  let fenced: {marker: '`' | '~'; length: number} | undefined;
  for (const line of source.split('\n')) {
    const start = offset;
    const end = start + line.replace(/\r$/, '').length;
    offset += line.length + 1;
    const marker = /^\s{0,3}(`{3,}|~{3,})/.exec(line)?.[1];
    const insideFence = Boolean(fenced);
    if (marker) {
      const kind = marker[0] as '`' | '~';
      if (!fenced) fenced = {marker: kind, length: marker.length};
      else if (kind === fenced.marker && marker.length >= fenced.length) fenced = undefined;
    }
    const directive =
      !insideFence &&
      /^:(?:media|row|panel|reveal|role|lead|secondary|callout)(?:\s+.*)?$/i.test(
        line.trim(),
      );
    const transformed = insideFence || marker ? line : line.replace(
      CUSTOM_MEDIA,
      (_whole, alt, kind, mediaSource, title = '', attrs = '') => {
        const index = media.push({alt, kind, source: mediaSource, title, attrs}) - 1;
        return `\n\nSLIDES2_MEDIA_${index}_TOKEN\n\n`;
      },
    );
    for (const text of (directive ? `\n${transformed}\n` : transformed).split('\n')) {
      lines.push({text, start, end});
    }
  }
  return {source: lines.map(line => line.text).join('\n'), media, lines};
}

function parseAttrs(raw: string): Record<string, string | boolean> {
  const attrs: Record<string, string | boolean> = {};
  for (const match of raw.matchAll(/([\w-]+)(?:=(?:"([^"]*)"|'([^']*)'|([^\s]+)))?/g)) {
    attrs[match[1]] = match[2] ?? match[3] ?? match[4] ?? true;
  }
  return attrs;
}

function attrsAfter(element: Element): {attrs: Record<string, string | boolean>; node?: Node} {
  const next = element.nextSibling;
  if (next?.nodeType !== Node.TEXT_NODE) return {attrs: {}};
  const match = /^\s*\{([^}]*)\}/.exec(next.textContent ?? '');
  if (!match) return {attrs: {}};
  next.textContent = (next.textContent ?? '').slice(match[0].length);
  return {attrs: parseAttrs(match[1]), node: next};
}

function caption(figure: HTMLElement, text: string, mode: string | boolean | undefined): void {
  if (!text || mode === 'none') return;
  const label = document.createElement('figcaption');
  label.textContent = text;
  figure.dataset.caption = String(mode || 'below');
  figure.append(label);
}

function decorateImages(root: HTMLElement, options: RenderOptions): void {
  for (const image of [...root.querySelectorAll('img')]) {
    const paragraph = image.parentElement?.tagName === 'P' ? image.parentElement : null;
    const {attrs} = attrsAfter(image);
    const figure = document.createElement('figure');
    figure.className = 'slide-media slide-image';
    const source = image.getAttribute('src') ?? '';
    if (/\.(mp4|webm|mov)(?:[?#]|$)/i.test(source)) {
      figure.className = 'slide-media slide-video';
      figure.dataset.asset = source;
      figure.append(createVideo(options.resolveAsset(source), image.alt || image.title, attrs));
      caption(figure, image.title, attrs.caption);
      image.replaceWith(figure);
      if (paragraph && paragraph.childNodes.length === 1) paragraph.replaceWith(figure);
      continue;
    }
    figure.dataset.asset = source;
    image.src = options.resolveAsset(source);
    image.loading = 'eager';
    image.decoding = 'async';
    image.alt ||= 'Slide image';
    if (attrs.mirror === 'x') {
      image.classList.add('mirror-x');
      figure.classList.add('mirror-x');
    }
    if (attrs.fit) figure.dataset.fit = String(attrs.fit);
    if (attrs.align) figure.dataset.align = String(attrs.align);
    if (attrs.size) figure.dataset.size = String(attrs.size);
    if (attrs.span) figure.dataset.span = String(attrs.span);
    let cropAspect: {width: number; height: number} | undefined;
    if (attrs.crop) {
      const [x, y, width, height] = String(attrs.crop).split(',').map(Number);
      if ([x, y, width, height].every(Number.isFinite)) {
        cropAspect = {width, height};
        figure.classList.add('has-crop');
        figure.style.setProperty('--crop-x', String(x));
        figure.style.setProperty('--crop-y', String(y));
        figure.style.setProperty('--crop-w', String(width));
        figure.style.setProperty('--crop-h', String(height));
      }
    }
    const setAspect = () => {
      if (!image.naturalWidth || !image.naturalHeight) return;
      const aspect = cropAspect
        ? (image.naturalWidth * cropAspect.width) / (image.naturalHeight * cropAspect.height)
        : image.naturalWidth / image.naturalHeight;
      figure.style.setProperty('--media-aspect', String(aspect));
    };
    image.addEventListener('load', setAspect, {once: true});
    if (image.complete) setAspect();
    image.replaceWith(figure);
    if (cropAspect) {
      const viewport = document.createElement('div');
      viewport.className = 'crop-viewport';
      viewport.append(image);
      figure.append(viewport);
    } else {
      figure.append(image);
    }
    caption(figure, image.title, attrs.caption);
    if (paragraph && paragraph.childNodes.length === 1) paragraph.replaceWith(figure);
  }
  for (const paragraph of [...root.querySelectorAll(':scope > p')]) {
    const meaningfulText = [...paragraph.childNodes]
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .some((node) => node.textContent?.trim());
    const children = [...paragraph.children];
    if (!meaningfulText && children.length && children.every((child) => child.tagName === 'FIGURE')) {
      paragraph.replaceWith(...children);
    }
  }
}

function decorateLinks(root: HTMLElement): void {
  for (const link of root.querySelectorAll<HTMLAnchorElement>('a[href]')) {
    const destination = new URL(link.href, location.href);
    if (!['http:', 'https:'].includes(destination.protocol)) continue;
    if (destination.origin === location.origin) continue;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
  }
}

function decorateCustomMedia(
  root: HTMLElement,
  customMedia: CustomMedia[],
  options: RenderOptions,
): HTMLElement[] {
  const animationTargets: HTMLElement[] = [];
  for (const placeholder of [...root.querySelectorAll('p')]) {
    const match = /^SLIDES2_MEDIA_(\d+)_TOKEN$/.exec(placeholder.textContent?.trim() ?? '');
    if (!match) continue;
    const item = customMedia[Number(match[1])];
    if (!item) continue;
    const {kind, source, title, alt} = item;
    const attrs = parseAttrs(item.attrs);
    const figure = document.createElement('figure');
    figure.className = `slide-media slide-${kind}`;
    figure.dataset.asset = source;

    if (kind === 'animation') {
      const target = document.createElement('div');
      target.className = 'animation-target';
      target.dataset.component = source;
      target.setAttribute('aria-label', alt);
      figure.append(target);
      animationTargets.push(target);
    } else {
      const resolved = source.startsWith('assets/') ? options.resolveAsset(source) : source;
      figure.append(createVideo(resolved, alt || title, attrs));
    }
    caption(figure, title, attrs.caption);
    if (kind === 'video') {
      const link = videoLink(source, attrs);
      if (link) figure.append(link);
    }
    placeholder.replaceWith(figure);
  }
  return animationTargets;
}

function applyDirectives(root: HTMLElement, visibleStep: number): void {
  let revealStep = 0;
  for (const element of [...root.children]) {
    if (element.parentElement !== root) continue;
    const text = element.textContent?.trim() ?? '';
    const roleMatch =
      element.tagName === 'P'
        ? /^:(?:(?:role\s+)?(lead|secondary|callout))$/i.exec(text)
        : null;
    if (roleMatch) {
      const target = element.nextElementSibling;
      if (target) target.setAttribute('data-role', roleMatch[1].toLowerCase());
      element.remove();
      continue;
    }
    const mediaMatch = element.tagName === 'P' ? /^:media(?:\s+(.*))?$/i.exec(text) : null;
    if (mediaMatch) {
      const attrs = parseAttrs(mediaMatch[1] ?? '');
      const group = document.createElement('div');
      group.className = 'media-group';
      for (const [key, value] of Object.entries(attrs)) group.dataset[key] = String(value);
      let sibling = element.nextElementSibling;
      while (sibling?.classList.contains('slide-media')) {
        const next = sibling.nextElementSibling;
        group.append(sibling);
        sibling = next;
      }
      element.replaceWith(group);
      continue;
    }
    if (element.tagName === 'P' && /^:row$/i.test(text)) {
      const breakElement = document.createElement('div');
      breakElement.className = 'media-row-break';
      breakElement.setAttribute('aria-hidden', 'true');
      element.replaceWith(breakElement);
      continue;
    }
    const panelMatch = element.tagName === 'P' ? /^:panel(?:\s+(.*))?$/i.exec(text) : null;
    if (panelMatch) {
      const attrs = parseAttrs(panelMatch[1] ?? '');
      const panel = document.createElement('div');
      panel.className = 'content-panel';
      if (attrs.span) panel.dataset.span = String(attrs.span);
      if (attrs.variant) panel.dataset.variant = String(attrs.variant);
      let sibling = element.nextElementSibling;
      while (sibling) {
        const next = sibling.nextElementSibling;
        const siblingText = sibling.textContent?.trim() ?? '';
        if (
          sibling.classList.contains('slide-media') ||
          (sibling.tagName === 'P' && siblingText.startsWith(':'))
        ) break;
        panel.append(sibling);
        sibling = next;
      }
      element.replaceWith(panel);
      if (attrs.reveal) {
        revealStep += 1;
        panel.dataset.revealStep = String(revealStep);
        panel.classList.toggle('is-hidden', revealStep > visibleStep);
      }
      continue;
    }
    const revealMatch =
      element.tagName === 'P' ? /^:reveal(?:\s+(next|each|\d+))?$/i.exec(text) : null;
    if (!revealMatch) continue;
    const explicit = Number(revealMatch[1]);
    if (Number.isFinite(explicit) && explicit > 0) revealStep = Math.max(revealStep, explicit);
    else revealStep += 1;
    const target = element.nextElementSibling;
    const each = revealMatch[1]?.toLowerCase() === 'each';
    if (target) {
      let targets: Element[];
      if (each && (target.tagName === 'UL' || target.tagName === 'OL')) {
        targets = [...target.children];
      } else if (each && target.classList.contains('slide-media')) {
        targets = [];
        let sibling: Element | null = target;
        while (sibling) {
          if (sibling.classList.contains('slide-media')) targets.push(sibling);
          else if (!sibling.classList.contains('media-row-break')) break;
          sibling = sibling.nextElementSibling;
        }
      } else {
        targets = [target];
      }
      for (const [index, child] of targets.entries()) {
        const step = revealStep + index;
        (child as HTMLElement).dataset.revealStep = String(step);
        (child as HTMLElement).classList.toggle('is-hidden', step > visibleStep);
      }
      if (each) revealStep += Math.max(0, targets.length - 1);
    }
    element.remove();
  }
}

function decorateSemanticRoles(root: HTMLElement): void {
  root.querySelector(':scope > h1')?.setAttribute('data-role', 'title');
  for (const media of root.querySelectorAll(':scope > .slide-media, :scope > .media-group')) {
    if (!media.hasAttribute('data-role')) media.setAttribute('data-role', 'media');
  }
  for (const callout of root.querySelectorAll(':scope > blockquote')) {
    if (!callout.hasAttribute('data-role')) callout.setAttribute('data-role', 'callout');
  }
  for (const paragraph of root.querySelectorAll<HTMLElement>(':scope > p')) {
    if (paragraph.hasAttribute('data-role')) continue;
    const children = [...paragraph.children];
    const textNodes = [...paragraph.childNodes].filter(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim(),
    );
    if (children.length === 1 && children[0].tagName === 'STRONG' && !textNodes.length) {
      paragraph.dataset.role = 'lead';
    }
  }
  for (const lead of root.querySelectorAll<HTMLElement>(':scope > [data-role="lead"]')) {
    const secondary = lead.nextElementSibling;
    if (secondary?.tagName === 'P' && !secondary.hasAttribute('data-role')) {
      secondary.setAttribute('data-role', 'secondary');
    }
  }
}

function decorateChecklist(root: HTMLElement): void {
  for (const heading of [...root.querySelectorAll(':scope > h2')]) {
    if (heading.parentElement !== root) continue;
    const column = document.createElement('section');
    column.className = 'checklist-column';
    let sibling: Element | null = heading;
    while (sibling && (sibling === heading || sibling.tagName !== 'H2')) {
      const next: Element | null = sibling.nextElementSibling;
      column.append(sibling);
      sibling = next;
    }
    root.insertBefore(column, sibling);
  }
}

function decorateMediaRows(root: HTMLElement): void {
  const children = [...root.children];
  const first = children.findIndex(
    (element) =>
      element.classList.contains('slide-media') ||
      element.classList.contains('content-panel') ||
      element.classList.contains('media-row-break'),
  );
  if (first < 0) return;
  const participants: Element[] = [];
  for (const element of children.slice(first)) {
    if (
      element.classList.contains('slide-media') ||
      element.classList.contains('content-panel') ||
      element.classList.contains('media-row-break')
    ) {
      participants.push(element);
      continue;
    }
    break;
  }
  if (!participants.some((element) => element.classList.contains('slide-media'))) return;

  const rows = document.createElement('div');
  rows.className = 'media-rows';
  root.insertBefore(rows, children[first]);
  let row = document.createElement('div');
  row.className = 'media-row';
  rows.append(row);
  for (const element of participants) {
    if (element.classList.contains('media-row-break')) {
      element.remove();
      if (row.children.length) {
        row = document.createElement('div');
        row.className = 'media-row';
        rows.append(row);
      }
      continue;
    }
    row.append(element);
  }
  if (!row.children.length) row.remove();
}

function decorateTextMedia(root: HTMLElement): void {
  const copyChildren = [...root.children].filter(
    (element) =>
      element.tagName !== 'H1' &&
      !element.classList.contains('slide-media') &&
      !element.classList.contains('content-panel'),
  );
  if (!copyChildren.length) return;

  const copy = document.createElement('div');
  copy.className = 'slide-copy';
  root.insertBefore(copy, copyChildren[0]);
  copy.append(...copyChildren);
}

export function renderSlideBody(
  slide: Slide,
  visibleStep: number,
  options: RenderOptions,
): {root: HTMLElement; animationTargets: HTMLElement[]} {
  const root = document.createElement('div');
  root.className = 'slide-body';
  const prepared = preprocess(slide.markdown);
  const tokens = markdown.parse(prepared.source, {});
  for (const token of tokens) {
    if (!token.map || !['heading_open', 'paragraph_open', 'list_item_open', 'blockquote_open'].includes(token.type)) continue;
    const start = prepared.lines[token.map[0]]?.start;
    const end = prepared.lines[token.map[1] - 1]?.end;
    if (start === undefined || end === undefined) continue;
    const raw = slide.markdown.slice(start, end);
    if (/!\[|^:/m.test(raw)) continue;
    token.attrSet('data-source-from', String(start));
    token.attrSet('data-source-to', String(end));
  }
  root.innerHTML = markdown.renderer.render(tokens, markdown.options, {});
  decorateLinks(root);
  decorateImages(root, options);
  const animationTargets = decorateCustomMedia(root, prepared.media, options);
  applyDirectives(root, visibleStep);
  decorateSemanticRoles(root);
  if (options.structure === 'checklist') decorateChecklist(root);
  if (options.structure === 'media-rows') decorateMediaRows(root);
  if (options.structure === 'text-media') decorateTextMedia(root);
  for (const item of root.querySelectorAll('ol > li')) {
    const body = document.createElement('div');
    body.className = 'card-body';
    body.append(...item.childNodes);
    item.append(body);
  }
  return {root, animationTargets};
}
